import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/CombinedAuthContext";

const LoginCallback = () => {
  const [status, setStatus] = useState("Initializing..."); // Initial status
  const [error, setError] = useState(null);
  const { processLoginCallback, client, loading: authLoading } = useAuth(); // Get client and loading state
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    async function handleLoginCallback() {
      if (!isMounted) return;

      // --- MODIFICATION START ---
      // 1. Wait for auth context (and client) to be ready
      if (authLoading || !client) {
        console.log(
          "[DEBUG] [LoginCallback] Waiting for Auth Context/Client..."
        );
        setStatus("Initializing authentication..."); // Update status while waiting
        // Optionally, check again after a short delay if stuck? Or rely on re-render when context updates.
        return; // Exit useEffect for now, will re-run when context updates
      }
      console.log("[DEBUG] [LoginCallback] Auth Context/Client ready.");
      setStatus("Verifying authentication..."); // Update status now that client is ready

      // 2. Parse URL using the client from context.
      //    The Wix SDK's parseFromUrl defaults to responseMode='fragment'
      //    (reads location.hash). Our signup flow requests responseMode='query',
      //    so Wix returns code/state as querystring params instead. Detect which
      //    one the current URL is using and pass the matching mode explicitly,
      //    otherwise the signup callback silently looks like a missing code.
      let returnedOAuthData;
      try {
        const currentUrl = window.location.href;
        const parsedUrl = new URL(currentUrl);
        const queryHasAuth =
          parsedUrl.searchParams.has("code") ||
          parsedUrl.searchParams.has("error");
        const responseMode = queryHasAuth ? "query" : "fragment";
        returnedOAuthData = client.auth.parseFromUrl(currentUrl, responseMode);
        console.log(
          "[DEBUG] [LoginCallback] Parsed URL Data:",
          returnedOAuthData,
          "(responseMode:",
          responseMode + ")"
        );
      } catch (parseError) {
        console.error("[DEBUG] [LoginCallback] Error parsing URL:", parseError);
        setError("Failed to parse authentication response from URL.");
        setStatus("Login failed");
        if (isMounted) navigate("/", { replace: true }); // Redirect home on parse error
        return;
      }

      // 3. Check if code and state exist
      if (returnedOAuthData?.code && returnedOAuthData?.state) {
        console.log("[DEBUG] [LoginCallback] Code and State found.");

        // 4. Check if already processed
        const callbackProcessed = sessionStorage.getItem(
          "oauth_callback_processed"
        );
        if (callbackProcessed === "true") {
          console.log(
            "[DEBUG] [LoginCallback] Callback already processed, navigating home."
          );
          if (isMounted) navigate("/", { replace: true });
          return;
        }

        // 5. Check for OAuth redirect data in localStorage
        const oauthRedirectData = JSON.parse(
          localStorage.getItem("oauthRedirectData") || "null"
        );
        if (!oauthRedirectData) {
          console.error(
            "[DEBUG] [LoginCallback] Missing OAuth redirect data from localStorage."
          );
          setError(
            "Authentication session data missing. Please try logging in again."
          );
          setStatus("Login failed");
          return; // Stay on page to show error
        }

        // 6. Mark as processed and call the main processing function
        console.log(
          "[DEBUG] [LoginCallback] Setting flag and calling processLoginCallback."
        );
        sessionStorage.setItem("oauth_callback_processed", "true");
        setStatus("Processing login...");

        try {
          // processLoginCallback now uses the client from context implicitly
          const result = await processLoginCallback();
          console.log(
            "[DEBUG] [LoginCallback] processLoginCallback result:",
            result
          );

          if (!isMounted) return;

          if (result.success) {
            setStatus("Login successful! Redirecting...");
            console.log(
              "[DEBUG] [LoginCallback] Redirecting to:",
              result.redirectUri || "/"
            );
            // Clear the flag on successful processing *before* navigating
            sessionStorage.removeItem("oauth_callback_processed");
            localStorage.removeItem("oauthRedirectData"); // Also clear this now
            setTimeout(() => {
              if (isMounted)
                navigate(result.redirectUri || "/", { replace: true });
            }, 300); // Slightly shorter delay
          } else {
            sessionStorage.removeItem("oauth_callback_processed"); // Clear flag on failure
            localStorage.removeItem("oauthRedirectData"); // Clear redirect data too
            setStatus("Login failed");
            setError(result.error || "Unknown error processing login");
            console.error(
              "[DEBUG] [LoginCallback] processLoginCallback failed:",
              result.error
            );
          }
        } catch (processError) {
          if (!isMounted) return;
          sessionStorage.removeItem("oauth_callback_processed"); // Clear flag on exception
          localStorage.removeItem("oauthRedirectData");
          setStatus("Login failed");
          setError(processError.message || "Unknown error during processing");
          console.error(
            "[DEBUG] [LoginCallback] Exception during processLoginCallback:",
            processError
          );
        }
      } else if (returnedOAuthData?.error) {
        console.error(
          "[DEBUG] [LoginCallback] OAuth Error in URL:",
          returnedOAuthData.error,
          returnedOAuthData.errorDescription
        );
        setError(
          `Authentication failed: ${
            returnedOAuthData.errorDescription || returnedOAuthData.error
          }`
        );
        setStatus("Login failed");
        sessionStorage.removeItem("oauth_callback_processed"); // Clear flag
        localStorage.removeItem("oauthRedirectData");
      } else {
        // 7. If no code/state, wait briefly for potential redirect, then show error if needed
        console.log("[DEBUG] [LoginCallback] No Code/State in URL yet.");
        const intendedLogin = localStorage.getItem("oauthRedirectData");
        if (intendedLogin) {
          // We intended to login, maybe the redirect is just slow or blocked
          setStatus("Waiting for redirect..."); // Give user feedback
          // Set a timeout to show error if redirect doesn't happen
          const timer = setTimeout(() => {
            if (isMounted && !returnedOAuthData?.code) {
              // Check again in case it updated
              console.error(
                "[DEBUG] [LoginCallback] Redirect timeout, assuming failure."
              );
              setError(
                "Redirect to authentication provider failed or was blocked. Please ensure pop-ups are allowed and try again."
              );
              setStatus("Login failed");
              localStorage.removeItem("oauthRedirectData"); // Clear intent
            }
          }, 5000); // Wait 5 seconds

          // Cleanup timer if component unmounts or redirect happens
          return () => clearTimeout(timer);
        } else {
          // Landed here without intent, go home.
          console.log(
            "[DEBUG] [LoginCallback] No login intended, navigating home."
          );
          if (isMounted) navigate("/", { replace: true });
        }
      }
      // --- MODIFICATION END ---
    }

    handleLoginCallback();

    return () => {
      isMounted = false;
    };
    // Add client and authLoading to dependency array
  }, [processLoginCallback, navigate, client, authLoading]);

  return (
    <div className="login-callback-container">
      <div className="login-callback-content">
        <h2>Authentication</h2>
        <div className="status-container">
          <div className="status">{status}</div>
          {error && <div className="error">{error}</div>}
          {/* Keep spinner logic, maybe tie it to specific statuses */}
          {!error &&
            (status === "Processing login..." ||
              status === "Initializing authentication..." ||
              status === "Verifying authentication..." ||
              status === "Waiting for redirect...") && (
              <div className="loading-indicator">
                <div className="spinner"></div>{" "}
                {/* Use FontAwesome here if preferred */}
              </div>
            )}
          {error && (
            <button
              onClick={() => {
                // Attempt login again, clearing flags
                localStorage.removeItem("oauthRedirectData");
                sessionStorage.removeItem("oauth_callback_processed");
                // Potentially call the login function from context if available?
                // Or just navigate home for user to click login manually
                navigate("/", { replace: true });
              }}
              style={{ marginTop: "15px" }}
            >
              Try Again / Go Home
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginCallback;
